import ReactPlayer from "react-player";
import {useNavigate} from "react-router-dom";
import {useEffect, useRef, useState} from "react";
import {AppFlex} from "shared/UI/UIKit/Flex";
import {AppText} from "shared/UI/UIKit/Text";
import {AppButton} from "shared/UI/UIKit/Button";
import {AppImage} from "shared/UI/UIKit/Image";
import {AppVolumeIcon} from "shared/UI/UIKit/icons/Volume";
import {
  getHomeUrl,
  getAnnotationUrl,
  getInteractiveUrl,
  getLiteratureUrl,
  getMethodologicalModuleUrl, getPracticalModuleUrl,
  getTargetUrl, getTestUrl, getTheoryUrl, getVideoUrl, getAdditionalMaterialsUrl
} from "services/navigation";
import type {
  InteractivePage, MethodologicalModulePage as MethodologicalModulePageType,
  Page,
  PracticalModulePage as PracticalModulePageType,
  PracticalModuleStep as PracticalModuleStepType
, TheoryPage as TheoryPageType} from "../../../../config/types";
import {PageType} from "../../../../config/types";
import {GLOBAL_CONFIG} from "../../../../config/config";
import styles from "./styles.module.scss";

type Props = { practicalModule: PracticalModulePageType}

export const SecondPracticalModule = ({practicalModule: module}: Props) => {
  const maxStepValue = 3;
  const navigate = useNavigate();
  const audioElement = useRef<HTMLAudioElement | null>(null);
  const [step, setStep] = useState(0);
  const [currentStep, setCurrentStep] = useState<PracticalModuleStepType>();
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentAudioUrl, setCurrentAudioUrl] = useState<string>();

  if (!module) {
	return null;
  }

  useEffect(() => {
    setCurrentStep(module.steps[step]);
	setCurrentAudioUrl(module.steps[step].audioUrl);

	return () => {
      setStep(0);
      setCurrentStep(module.steps[0]);
      setIsPlaying(false);
	};
  }, []);

  const getUrl = (page: Page) => {
	switch (page.type) {
      case PageType.ANNOTATION:
		return getAnnotationUrl();
      case PageType.INTERACTIVE:
		return getInteractiveUrl({id: (page as InteractivePage).id});
      case PageType.LITERATURE:
		return getLiteratureUrl();
      case PageType.TARGET:
		return getTargetUrl();
      case PageType.TEST:
		return getTestUrl();
      case PageType.THEORY:
		return getTheoryUrl({id: (page as TheoryPageType).id});
      case PageType.VIDEO:
		return getVideoUrl();
      case PageType.METHODOLOGICAL_MODULE:
		return getMethodologicalModuleUrl({id: (page as MethodologicalModulePageType).id});
      case PageType.PRACTICAL_MODULE:
		return getPracticalModuleUrl({id: (page as PracticalModulePageType).id});
      case PageType.ADDITIONAL_MATERIALS:
        return getAdditionalMaterialsUrl();
      default:
		return getHomeUrl();
	}
  };

  const goToPrevStep = () => {
    if (step === 0) {
      const prevPage = GLOBAL_CONFIG.pages.find(page => page.order === module.order - 1);

      if (prevPage) {
		const url = getUrl(prevPage);
		navigate(url);
      }
	} else {
      setStep(step - 1);
      setCurrentStep(module.steps[step - 1]);
	}
  };

  const goToNextStep = () => {
	if (step < maxStepValue) {
      setStep(step + 1);
      setCurrentStep(module.steps[step + 1]);
	} else {
      const nextPage = GLOBAL_CONFIG.pages.find(page => page.order === module.order + 1);

      if (nextPage) {
		const url = getUrl(nextPage);
		navigate(url);
      }
	}
  };

  const togglePlay = async (url: string) => {
	if (audioElement) {
      if (url !== currentAudioUrl) {
        await setCurrentAudioUrl(url);
        setIsPlaying(true);
		audioElement?.current?.load();
        await audioElement?.current?.play();
      } else {
        if (isPlaying) {
          setIsPlaying(false);
          audioElement?.current?.pause();
        } else {
          setIsPlaying(true);
          await audioElement?.current?.play();
        }
      }
	}
  };

  return (
    <AppFlex>
      {currentStep?.audioUrl && <audio ref={audioElement} src={currentAudioUrl}></audio>}
      {step === 0 && (
        <AppFlex className={styles.step}>
          <AppFlex direction="row">
            <AppFlex>
              <AppVolumeIcon className={styles.volumeIcon} onClick={() => togglePlay(currentStep!.audioUrl as string)}/>
            </AppFlex>
            <AppFlex>
              <AppText size={18}>Уважаемые коллеги!</AppText>
              <AppText size={18}>{currentStep?.text}</AppText>
              <AppFlex direction="row" className={styles.columns}>
                {currentStep?.opinions && currentStep.opinions.map((item, idx) => (
                  <AppFlex key={idx} className={styles.paragraph}>
                    <AppText size={18} className={styles.opinion}>{item.text}</AppText>
                    {item.paragraphs.map((paragraph, subIdx) => (
                      <AppText className={styles.boldText} key={"paragraph" + subIdx} size={18}>{paragraph}</AppText>
                    ))}
                  </AppFlex>
				))}
              </AppFlex>
            </AppFlex>
          </AppFlex>
          <AppFlex direction="row" className={styles.offsetText}>
            <AppFlex>
              <AppVolumeIcon className={styles.volumeIcon} onClick={() => togglePlay(currentStep!.additionalAudioUrl as string)}/>
            </AppFlex>
            <AppText size={18}>{currentStep?.additionalText}</AppText>
          </AppFlex>
          <AppFlex>
            <div className={styles.action}>
              <AppButton className={styles.prevStepBtn} onClick={goToPrevStep}>{currentStep?.prevBtnName}</AppButton>
              <AppButton className={styles.nextStepBtn} onClick={goToNextStep}>{currentStep?.nextBtnName}</AppButton>
            </div>
          </AppFlex>
        </AppFlex>
      )}
      {step === 1 && (
        <AppFlex className={styles.step}>
          {/*<AppText className={styles.videoLabel} size={18}>Видеофрагмент урока</AppText>*/}
          <ReactPlayer
			className={styles.video}
			controls
			config={{
              file: {
				attributes: {
                  disablePictureInPicture: true,
                  controlsList: "nodownload noremoteplayback noplaybackrate"
				}
              }
			}}
			url={currentStep?.videoUrl}/>
          <div className={styles.action}>
            <AppButton className={styles.prevStepBtn} onClick={goToPrevStep}>{currentStep?.prevBtnName}</AppButton>
            <AppButton className={styles.nextStepBtn} onClick={goToNextStep}>{currentStep?.nextBtnName}</AppButton>
          </div>
        </AppFlex>
      )}
      {step === 2 && (
        <AppFlex direction="row" className={styles.step}>
          <AppFlex>
            <AppVolumeIcon className={styles.volumeIcon} onClick={() => togglePlay(currentStep!.audioUrl as string)}/>
          </AppFlex>
          <AppFlex>
            <AppText size={18} className={styles.boldText}>{currentStep?.text}</AppText>
            <AppImage src={currentStep?.imagePath} alt="image" className={styles.image}/>
            <div className={styles.action}>
              <AppButton className={styles.prevStepBtn} onClick={goToPrevStep}>{currentStep?.prevBtnName}</AppButton>
              <AppButton className={styles.nextStepBtn} onClick={goToNextStep}>{currentStep?.nextBtnName}</AppButton>
            </div>
          </AppFlex>
        </AppFlex>
      )}
      {step === 3 && (
        <AppFlex>
          <ReactPlayer
			className={styles.video}
			controls
			config={{
              file: {
                attributes: {
                  disablePictureInPicture: true,
                  controlsList: "nodownload noremoteplayback noplaybackrate"
                }
              }
            }}
			url={currentStep?.videoUrl}/>
          <div className={styles.action}>
            <AppButton className={styles.prevStepBtn} onClick={goToPrevStep}>{currentStep?.prevBtnName}</AppButton>
            <AppButton className={styles.nextStepBtn} onClick={goToNextStep}>{currentStep?.nextBtnName}</AppButton>
          </div>
        </AppFlex>
      )}
    </AppFlex>
  );
};
